// Full visual-enhanced version of BusinessEditor with cues, symmetry, and accessible contrast

import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, firestore } from '../views/firebaseConfig';

const CreateEventView = ({ editMode = false, eventId, onClose, setActiveView }) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fee, setFee] = useState('');
  const [rules, setRules] = useState('');
  const [numPlayers, setNumPlayers] = useState('');
  const [prize, setPrize] = useState('');
  const [ballType, setBallType] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [error, setError] = useState('');
  const [businessProfile, setBusinessProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(editMode);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    const fetchBusinessProfile = async () => {
      const user = auth.currentUser;
      if (user) {
        const docRef = doc(firestore, 'businessprofiles', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setBusinessProfile(docSnap.data());
        } else {
          setError('Business profile not found.');
        }
      }
    };

    const fetchEventData = async () => {
      if (editMode && eventId) {
        try {
          const docRef = doc(firestore, 'tournaments', eventId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const eventData = docSnap.data();
            setTitle(eventData.title);
            setDescription(eventData.description);
            setFee(eventData.fee);
            setRules(eventData.rules);
            setNumPlayers(eventData.numPlayers);
            setPrize(eventData.prize);
            setBallType(eventData.ballType);
            setSelectedDate(eventData.dateTime.toDate().toISOString().slice(0, 10));
            setSelectedTime(eventData.dateTime.toDate().toISOString().slice(11, 16));
            setIsLoading(false);
          } else {
            setError('Event not found.');
          }
        } catch (error) {
          console.error('Failed to load event data:', error);
          setError('Failed to load event data.');
        }
      }
    };

    fetchBusinessProfile();
    fetchEventData();
  }, [editMode, eventId]);

  const isPostButtonDisabled = () => {
    const currentDate = new Date();
    const selectedDateTime = new Date(`${selectedDate}T${selectedTime}`);
    const timeDifference = (selectedDateTime - currentDate) / 60000;

    return (
      timeDifference < 30 ||
      title.trim() === '' ||
      description.trim() === '' ||
      fee.trim() === '' ||
      rules.trim() === '' ||
      numPlayers.trim() === '' ||
      prize.trim() === '' ||
      ballType.trim() === '' ||
      !selectedDate ||
      !selectedTime
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPostButtonDisabled()) {
      setError('Please ensure all fields are filled and the event is at least 30 minutes in the future.');
      return;
    }

    try {
      const currentUserID = auth.currentUser?.uid;
      if (!currentUserID || !businessProfile) {
        setError('Failed to fetch user or business profile data.');
        return;
      }

      const eventData = {
        title,
        description,
        fee,
        rules,
        numPlayers,
        prize,
        ballType,
        dateTime: new Date(`${selectedDate}T${selectedTime}`),
        timestamp: serverTimestamp(),
        status: 'active',
        businessId: currentUserID,
        address: businessProfile.address || '',
        businessEmail: businessProfile.businessEmail || '',
        businessName: businessProfile.businessName || '',
        businessBrand: businessProfile.businessBrand || ''
      };

      if (editMode) {
        const docRef = doc(firestore, 'tournaments', eventId);
        await updateDoc(docRef, eventData);
      } else {
        const tournamentsCollection = collection(firestore, 'tournaments');
        const newDocRef = await addDoc(tournamentsCollection, eventData);
        await updateDoc(newDocRef, { eventId: newDocRef.id });
      }

      setSaveSuccess(true);
      setTimeout(() => {
        setActiveView('postmanager');
        onClose();
      }, 800);
    } catch (error) {
      console.error('Error creating or updating the event:', error);
      setError('Error creating or updating the event. Please try again.');
    }
  };

  if (isLoading) {
    return <p className="text-center text-white">Loading event data...</p>;
  }

  return (
    <>
      {error && <div className="alert alert-danger text-center">{error}</div>}
      {saveSuccess && <div className="alert alert-success text-center">Event saved successfully!</div>}

      <form onSubmit={handleSubmit} className="row g-3 p-4">
        {[['Title', title, setTitle], ['Description', description, setDescription]].map(([label, val, setter], i) => (
          <div className="col-12" key={i}>
            <input
              type="text"
              placeholder={label}
              value={val}
              onChange={(e) => setter(e.target.value)}
              className="form-control bg-dark text-white rounded-pill px-4 py-2"
            />
          </div>
        ))}

        <div className="col-md-6">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="form-control bg-dark text-white rounded-pill px-4 py-2"
          />
        </div>
        <div className="col-md-6">
          <input
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            className="form-control bg-dark text-white rounded-pill px-4 py-2"
          />
        </div>

        {[fee, rules, numPlayers, prize, ballType].map((field, i) => (
          <div className="col-md-6" key={i}>
            <input
              type="text"
              placeholder={['Fee', 'Rules', 'Number of Players', 'Prize', 'Ball Type'][i]}
              value={field}
              onChange={(e) => {
                const setters = [setFee, setRules, setNumPlayers, setPrize, setBallType];
                setters[i](e.target.value);
              }}
              className="form-control bg-dark text-white rounded-pill px-4 py-2"
            />
          </div>
        ))}

        <div className="col-12 text-center">
          <button
            type="submit"
            className={`btn btn-${isPostButtonDisabled() ? 'secondary' : 'success'} px-5 py-2 rounded-pill`}
            disabled={isPostButtonDisabled()}
          >
            {editMode ? 'Update Event' : 'Create Event'}
          </button>
        </div>
      </form>
    </>
  );
};

export default CreateEventView;
